import { useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPencil,
  faTrash,
  faGears,
  faPrint,
  faArrowUpRightFromSquare,
} from '@fortawesome/free-solid-svg-icons';
import {
  equipmentService,
  type Equipment,
  type UpdateEquipmentPayload,
  type EquipmentStatus,
  type EquipmentCustomerSummaryRow,
} from '@/services/equipment.service';
import { CustomerInfoCard } from './components/CustomerInfoCard';
import { ROUTES } from '@/routes/routes';
import { manufacturerService } from '@/services/manufacturer.service';
import { equipmentTypeService } from '@/services/equipment-type.service';
import { EquipmentForm, STATUS_LABELS } from './components/EquipmentForm';
import { SkeletonPage } from '@/components/skeletons';
import { PageHeader } from '@/components/shared/PageHeader';
import { SearchBar } from '@/components/shared/SearchBar';
import { EmptyState } from '@/components/shared/EmptyState';
import { Pagination } from '@/components/shared/Pagination';
import { Modal } from '@/components/shared/Modal';
import { Button } from '@/components/ui/button';
import { toast, confirmDelete } from '@/lib/swal';
import { useDebounce } from '@/hooks/use-debounce';
import { useHasPermission } from '@/hooks/use-permissions';

const PAGE_SIZE = 20;

const STATUS_FILTERS: ReadonlyArray<{ value: EquipmentStatus | ''; label: string }> = [
  { value: '', label: 'All statuses' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'INACTIVE', label: 'Inactive' },
  { value: 'RETIRED', label: 'Retired' },
];

/** D-101 - the one customer identity rule used across the registry. */
const organisationOf = (row: {
  customerOrganizationName: string | null;
  customerName: string | null;
}) => row.customerOrganizationName ?? row.customerName;

const FILTER_SELECT_CLASS =
  'h-9 rounded-lg border border-border bg-background px-3 text-sm text-foreground ' +
  'focus:outline-none focus:ring-2 focus:ring-ring';

/** Asset lifecycle only. D-106 moved the SERVICE journey badge to Equipment Inward. */
const STATUS_BADGE: Record<EquipmentStatus, string> = {
  ACTIVE: 'bg-primary/10 text-primary',
  INACTIVE: 'bg-muted text-muted-foreground',
  RETIRED: 'bg-destructive/10 text-destructive',
};

function extractApiMessage(error: Error, fallback: string): string {
  if (axios.isAxiosError(error)) {
    const body = error.response?.data as { error?: { message?: string } } | undefined;
    if (body?.error?.message) return body.error.message;
  }
  return fallback;
}

export function EquipmentPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const can = useHasPermission();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 350);

  /**
   * D-103 - filter state lives in the URL using the router's existing
   * `useSearchParams`, the same pattern other pages already use. This is also
   * what will let a future Stage D link land on `/equipment?customerId=...` and
   * have the filter preselected, with no extra work. The drill-down itself is
   * NOT implemented here.
   */
  const [searchParams, setSearchParams] = useSearchParams();
  /**
   * D-106 - Equipment is the master/registry view. Workflow group, workflow
   * stage and receiving lot are OPERATIONAL scopes and now live on Equipment
   * Inward, so this page neither reads nor sends them. The backend still accepts
   * those parameters; the registry simply has no business setting them.
   */
  const customerId = searchParams.get('customerId') ?? '';
  const statusFilter = (searchParams.get('status') ?? '') as EquipmentStatus | '';

  const setFilter = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next, { replace: true });
    setPage(1);
  };

  const clearFilters = () => {
    setSearchParams(new URLSearchParams(), { replace: true });
    setSearch('');
    setPage(1);
  };

  const filtersActive = Boolean(customerId || statusFilter || debouncedSearch);
  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<Equipment | null>(null);

  const { data: manufacturers } = useQuery({
    queryKey: ['manufacturers-all'],
    queryFn: () => manufacturerService.list({ pageSize: 100 }),
  });
  const { data: types } = useQuery({
    queryKey: ['equipment-types-all'],
    queryFn: () => equipmentTypeService.list({ pageSize: 100 }),
  });

  /**
   * D-103 - every filter is sent to the server. Nothing is filtered in the
   * browser, so the summary and the page always describe the same population.
   */
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['equipment', page, debouncedSearch, customerId, statusFilter],
    queryFn: () =>
      equipmentService.list({
        page,
        pageSize: PAGE_SIZE,
        search: debouncedSearch || undefined,
        customerId: customerId || undefined,
        status: statusFilter || undefined,
      }),
  });

  // Server-supplied, tenant-scoped, and already filtered - never a separate
  // paginated customer fetch (the D-101 defect).
  const customerOptions: EquipmentCustomerSummaryRow[] = useMemo(
    () => (data?.customerSummary ?? []).filter((r) => r.customerId !== null),
    [data],
  );

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['equipment'] });

  const createMutation = useMutation({
    mutationFn: equipmentService.create,
    onSuccess: () => {
      toast.success('Equipment created');
      setModalOpen(false);
      invalidate();
    },
    onError: (error: Error) => {
      toast.error(extractApiMessage(error, 'Failed to create equipment'));
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateEquipmentPayload }) =>
      equipmentService.update(id, payload),
    onSuccess: () => {
      toast.success('Equipment updated');
      setModalOpen(false);
      setEditTarget(null);
      invalidate();
    },
    onError: (error: Error) => {
      toast.error(extractApiMessage(error, 'Failed to update equipment'));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: equipmentService.remove,
    onSuccess: () => {
      toast.success('Equipment deleted');
      invalidate();
    },
    onError: () => toast.error('Failed to delete equipment'),
  });

  /**
   * D-101 — customer identity comes from the equipment row itself.
   *
   * This previously did `customers?.items.find(...)` against a SEPARATELY
   * FETCHED, page-size-100 customer list, so any equipment whose customer fell
   * outside that page rendered a bare em-dash — a real customer shown as "no
   * customer". The Equipment API now resolves the relation server-side, so
   * there is nothing to look up and nothing to miss.
   *
   * `organizationName ?? name`: the registered organisation name is the
   * authoritative identity; the short trade name is the documented fallback for
   * legacy rows that predate it.
   */
  const getCustomerOrganisation = (eq: Equipment) => eq.customerOrganizationName ?? eq.customerName;
  const getManufacturerName = (id: string) =>
    manufacturers?.items.find((m) => m.id === id)?.name ?? '—';
  const getTypeName = (id: string) => types?.items.find((t) => t.id === id)?.name ?? '—';

  const handleDelete = async (eq: Equipment) => {
    if (await confirmDelete(eq.equipmentName)) deleteMutation.mutate(eq.id);
  };

  const handlePrintLabel = async (eq: Equipment) => {
    try {
      await equipmentService.downloadLabel(eq.id, eq.assetNumber);
      toast.success('Label PDF downloaded');
      invalidate();
    } catch {
      toast.error('Failed to generate label PDF');
    }
  };

  const openCreate = () => {
    setEditTarget(null);
    setModalOpen(true);
  };
  const openEdit = (eq: Equipment) => {
    setEditTarget(eq);
    setModalOpen(true);
  };
  const closeModal = () => {
    setModalOpen(false);
    setEditTarget(null);
  };
  const handleSearch = (v: string) => {
    setSearch(v);
    setPage(1);
  };

  const saving = createMutation.isPending || updateMutation.isPending;

  if (isLoading) return <SkeletonPage />;

  return (
    <div className="space-y-5">
      <PageHeader
        title="Equipment"
        description="Manage equipment registered for your customers"
        onAdd={can('equipment:create') ? openCreate : undefined}
        addLabel="Add Equipment"
      />
      <SearchBar
        value={search}
        onChange={handleSearch}
        placeholder="Search by name, asset number, or serial number..."
      />

      <div className="flex flex-wrap items-center gap-2">
        <select
          aria-label="Customer Organisation"
          className={FILTER_SELECT_CLASS}
          value={customerId}
          onChange={(e) => setFilter('customerId', e.target.value)}
        >
          <option value="">All customer organisations</option>
          {customerOptions.map((c) => (
            <option key={c.customerId ?? ''} value={c.customerId ?? ''}>
              {organisationOf(c)} ({c.total})
            </option>
          ))}
        </select>

        <select
          aria-label="Equipment Status"
          className={FILTER_SELECT_CLASS}
          value={statusFilter}
          onChange={(e) => setFilter('status', e.target.value)}
        >
          {STATUS_FILTERS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>

        {filtersActive ? (
          <Button variant="secondary" size="sm" onClick={clearFilters}>
            Clear Filters
          </Button>
        ) : null}
      </div>

      {/* D-104 - the drill-down context: who this equipment belongs to. */}
      {customerId ? <CustomerInfoCard customerId={customerId} /> : null}

      {isError ? (
        <p className="rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {extractApiMessage(error as Error, 'Failed to load equipment')}
        </p>
      ) : null}

      {data && data.total > 0 ? (
        <>
          <div className="overflow-x-auto rounded-lg border border-border">
            <table aria-label="Equipment" className="w-full min-w-[820px] text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/40 text-left">
                  <th className="px-4 py-3 font-medium text-foreground">
                    Model # / Serial / Asset
                  </th>
                  <th className="px-4 py-3 font-medium text-foreground">Customer Organisation</th>
                  <th className="px-4 py-3 font-medium text-foreground">Manufacturer</th>
                  <th className="px-4 py-3 font-medium text-foreground">Type</th>
                  <th className="px-4 py-3 font-medium text-foreground">Status</th>
                  <th className="px-4 py-3 font-medium text-foreground sr-only">Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((eq) => (
                  <tr
                    key={eq.id}
                    className="border-b border-border last:border-0 hover:bg-muted/20"
                  >
                    <td className="px-4 py-3">
                      <span className="font-mono font-medium text-foreground">
                        {eq.modelNumber ?? '—'}
                      </span>
                      <span className="text-muted-foreground">
                        {' '}
                        | {eq.serialNumber} | {eq.assetNumber}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {eq.customerId ? (
                        // D-106 (amends D-104) — the drill-down now lands on
                        // Equipment Inward, the operational view, rather than
                        // re-filtering this registry. The destination reads
                        // `customerId` from the URL and uses the EXISTING
                        // GET /equipment-intake customer filter.
                        <button
                          type="button"
                          onClick={() =>
                            navigate(
                              `${ROUTES.EQUIPMENT_INTAKE}?customerId=${encodeURIComponent(
                                eq.customerId ?? '',
                              )}`,
                            )
                          }
                          title={`View inward records for ${getCustomerOrganisation(eq) ?? 'this customer'}`}
                          className="text-left text-foreground underline-offset-4 hover:text-primary hover:underline"
                        >
                          {getCustomerOrganisation(eq) ?? '—'}
                        </button>
                      ) : (
                        <span className="text-xs font-medium text-primary/70">
                          Company Inventory
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {getManufacturerName(eq.manufacturerId)}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {getTypeName(eq.equipmentTypeId)}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_BADGE[eq.status]}`}
                      >
                        {STATUS_LABELS[eq.status]}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => navigate(`/equipment/${eq.id}`)}
                          className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground"
                          aria-label={`View ${eq.equipmentName}`}
                          title="View details"
                        >
                          <FontAwesomeIcon
                            icon={faArrowUpRightFromSquare}
                            className="h-3.5 w-3.5"
                          />
                        </button>
                        {can('equipment:print-label') && (
                          <button
                            onClick={() => handlePrintLabel(eq)}
                            className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground"
                            aria-label={`Print label for ${eq.equipmentName}`}
                            title="Print label"
                          >
                            <FontAwesomeIcon icon={faPrint} className="h-3.5 w-3.5" />
                          </button>
                        )}
                        {can('equipment:update') && (
                          <button
                            onClick={() => openEdit(eq)}
                            className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground"
                            aria-label={`Edit ${eq.equipmentName}`}
                            title="Edit"
                          >
                            <FontAwesomeIcon icon={faPencil} className="h-3.5 w-3.5" />
                          </button>
                        )}
                        {can('equipment:delete') && (
                          <button
                            onClick={() => handleDelete(eq)}
                            className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                            aria-label={`Delete ${eq.equipmentName}`}
                            title="Delete"
                            disabled={deleteMutation.isPending}
                          >
                            <FontAwesomeIcon icon={faTrash} className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination
            page={data.page}
            pageSize={data.pageSize}
            total={data.total}
            onPageChange={setPage}
          />
        </>
      ) : (
        <EmptyState
          icon={faGears}
          // D-103 - never claim the estate is empty when it is only the filters
          // that match nothing; the two need different actions from the operator.
          title={filtersActive ? 'No equipment matches your filters' : 'No equipment yet'}
          description={
            filtersActive
              ? 'Try clearing or changing the selected filters.'
              : 'Add your first equipment record to get started.'
          }
          action={
            filtersActive ? (
              <Button variant="secondary" onClick={clearFilters}>
                Clear Filters
              </Button>
            ) : can('equipment:create') ? (
              <Button onClick={openCreate}>Add Equipment</Button>
            ) : undefined
          }
        />
      )}

      <Modal
        open={modalOpen}
        onClose={closeModal}
        title={editTarget ? 'Edit Equipment' : 'Add Equipment'}
        size="lg"
      >
        <EquipmentForm
          initial={
            editTarget
              ? {
                  ownershipType: editTarget.ownershipType,
                  customerId: editTarget.customerId ?? undefined,
                  manufacturerId: editTarget.manufacturerId,
                  equipmentTypeId: editTarget.equipmentTypeId,
                  equipmentModelId: editTarget.equipmentModelId,
                  equipmentName: editTarget.equipmentName,
                  assetNumber: editTarget.assetNumber,
                  serialNumber: editTarget.serialNumber,
                  purchaseDate: editTarget.purchaseDate ?? undefined,
                  installationDate: editTarget.installationDate ?? undefined,
                  status: editTarget.status,
                  description: editTarget.description ?? undefined,
                  notes: editTarget.notes ?? undefined,
                }
              : undefined
          }
          onSubmit={(payload) =>
            editTarget
              ? updateMutation.mutate({ id: editTarget.id, payload })
              : createMutation.mutate(payload)
          }
          onCancel={closeModal}
          saving={saving}
        />
      </Modal>
    </div>
  );
}
